// src/pages/HostProfilePageBigPicture.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HostProfileCard from '../components/HostProfileCard';
import ArtworkCard from '../components/ArtworkCard';
import MiniArtistCard from '../components/MiniArtistCard';

import { publicGet } from '../lib/apiPublic';

function toAbsolute(src) {
  if (!src) return null;
  if (/^https?:\/\//i.test(src)) return src;
  const needsSlash = src[0] !== '/';
  return `${needsSlash ? '/' : ''}${src}`;
}

// Normalize any absolute media URL to a relative one so CRA proxy handles it.
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/media/')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url.replace(/^https?:\/\/[^/]+/, '');
  }
  const trimmed = url.replace(/^\.?\/*/, '');
  return trimmed.startsWith('media/') ? `/${trimmed}` : `/media/${trimmed}`;
}

function normalizeArtworkForCard(item) {
  // item may be HostArtwork or an Artwork
  const a = item?.artwork || item || {};
  // prefer explicit image_url; fall back to first image object
  const firstImg = Array.isArray(a.images) ? (a.images[0]?.url || a.images[0]?.image || a.images[0]?.image_url) : null;
  const image_url = a.primary_image_url || a.image_url || firstImg || null;
  return { ...a, image_url: toAbsolute(image_url) };
}

function HorizontalSlider({
  items,
  renderItem,
  emptyMessage,
  sliderGap,
}) {
  // 2mm-ish total between cards ≈ 8px
  const SLIDE_GAP_PX = 8; // total gap target
  const SLIDE_SIDE_PAD_PX = SLIDE_GAP_PX / 2; // 4px each side

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        mt: 2,
        overflowX: 'auto',
        pb: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: `${sliderGap}px`,
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        {items.map((item, idx) => (
          <Box
            key={item?.id || idx}
            sx={{
              height: '100%',
              minWidth: 260,
              flex: '0 0 auto',

              // ✅ this creates the small gap between slides
              px: `${SLIDE_SIDE_PAD_PX}px`,
              boxSizing: 'border-box',

              // ✅ kill any default margins inside cards
              '& .MuiCard-root': { m: 0 },
              '& .MuiPaper-root': { m: 0 }, // some cards are Paper
            }}
          >
            {renderItem(item, idx)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function HostProfilePageBigPicture() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [host, setHost] = useState(null);
  const [hostedArtworks, setHostedArtworks] = useState([]);

  // Public gallery representation data
  const [managedArtists, setManagedArtists] = useState([]);
  const [representedArtworks, setRepresentedArtworks] = useState([]);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const sliderGap = 6;

  const managedArtistsWithIds = useMemo(
    () =>
      managedArtists.filter((link) => link?.artist || link?.artist_id),
    [managedArtists],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [hostRes, hostedRes] = await Promise.all([
          publicGet(`/hosts/${id}/`, { params: { view: "card" } }),
          publicGet(`/hosts/hosted_artworks/`, { params: { id, view: "card" } }),
        ]);

        if (cancelled) return;

        const hostData = hostRes.data;
        setHost(hostData);
        setHostedArtworks(Array.isArray(hostedRes.data) ? hostedRes.data : []);

        // If host is a gallery, load its represented artists & artworks via public endpoints
        if (hostData?.venue_type === 'art_gallery') {
          try {
            const [maRes, mawRes] = await Promise.all([
              publicGet(`/hosts/${id}/managed-artists-public/`, { params: { view: "card" } }),
              publicGet(`/hosts/${id}/managed-artworks-public/`, { params: { view: "card" } }),
            ]);

            setManagedArtists(Array.isArray(maRes.data) ? maRes.data : []);
            setRepresentedArtworks(Array.isArray(mawRes.data) ? mawRes.data : []);
          } catch (err) {
            console.error('Failed to load public managed artists/artworks', err);
            setManagedArtists([]);
            setRepresentedArtworks([]);
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load host page data', e?.response?.data || e);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const isGalleryHost = host?.venue_type === 'art_gallery';

  const fullName = host
    ? (host.display_name ||
      `${host.user?.first_name || ''} ${host.user?.last_name || ''}`.trim() ||
      host.user?.username)
    : '';

  const heroImage = useMemo(() => {
    const venueImages = Array.isArray(host?.venue_images)
      ? host.venue_images
      : [];
    const venueSrc = venueImages
      .map((img) => img?.image || img?.url || img?.image_url)
      .find(Boolean);
    if (venueSrc) return toRelativeMedia(venueSrc);
    const profileSrc = host?.primary_profile_image || host?.user?.primary_profile_image;
    return profileSrc ? toRelativeMedia(profileSrc) : null;
  }, [host]);

  const scrollToSection = (sectionId) => () => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!host) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'grey.200',
          backgroundColor: 'transparent',
        }}
      >
        <Typography variant="h6">Loading host profile...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        color: '#f9fafb',
        backgroundColor: 'transparent',
        position: 'relative',
      }}
    >
      {/* Fixed top nav */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          backdropFilter: 'blur(14px)',
          backgroundColor: 'rgba(6, 12, 32, 0.86)',
          borderBottom: '1px solid rgba(148,163,184,0.35)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, letterSpacing: 1 }}
          >
            ARTflow
          </Typography>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: { xs: 2, md: 3 },
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              component="button"
              onClick={scrollToSection('intro-section')}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                color: 'rgba(209,213,219,0.95)',
                '&:hover': { color: '#7dd3fc' },
              }}
            >
              Intro
            </Typography>
            <Typography
              component="button"
              onClick={scrollToSection('profile-section')}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                color: 'rgba(209,213,219,0.95)',
                '&:hover': { color: '#7dd3fc' },
              }}
            >
              Profile
            </Typography>
            <Typography
              component="button"
              onClick={scrollToSection('hosted-section')}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                color: 'rgba(209,213,219,0.95)',
                '&:hover': { color: '#7dd3fc' },
              }}
            >
              Hosted Artworks
            </Typography>
            {isGalleryHost && (
              <Typography
                component="button"
                onClick={scrollToSection('represented-artists-section')}
                sx={{
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  color: 'rgba(209,213,219,0.95)',
                  '&:hover': { color: '#7dd3fc' },
                }}
              >
                Represented Artists
              </Typography>
            )}
            {isGalleryHost && (
              <Typography
                component="button"
                onClick={scrollToSection('represented-artworks-section')}
                sx={{
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  color: 'rgba(209,213,219,0.95)',
                  '&:hover': { color: '#7dd3fc' },
                }}
              >
                Represented Artworks
              </Typography>
            )}
          </Box>
        </Container>
      </Box>

      {/* Intro (full-screen hero) */}
      <Box
        id="intro-section"
        component="section"
        sx={{
          minHeight: { xs: '100vh', md: '100vh' },
          height: { xs: '100dvh', md: '100vh' },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          pt: { xs: 10, md: 12 },
          pb: { xs: 6, md: 8 },
          overflow: 'hidden',
        }}
      >
        {heroImage ? (
          <Box
            component="div"
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(6,12,32,0.90)), url(${heroImage})`,
              backgroundSize: 'cover',
              backgroundPosition: { xs: 'center top', md: 'center' },
              backgroundRepeat: 'no-repeat',
              filter: 'blur(3px)',
              transform: 'scale(1.03)',
              opacity: 0.85,
              zIndex: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at top, rgba(56,189,248,0.25), transparent 55%),' +
                'radial-gradient(circle at bottom, rgba(94,234,212,0.18), transparent 55%),' +
                'rgba(15,23,42,0.96)',
              zIndex: 0,
            }}
          />
        )}

        <Container
          maxWidth="md"
          sx={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Typography
            variant={isDesktop ? 'h2' : 'h3'}
            sx={{
              fontWeight: 700,
              mb: 2,
              textShadow: '0 18px 40px rgba(0,0,0,0.75)',
            }}
          >
            {fullName}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              maxWidth: 640,
              mx: 'auto',
              mb: 3,
              color: 'rgba(226,232,240,0.96)',
            }}
          >
            Discover this ARTflow host, explore their space, and view the
            collections they support.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={scrollToSection('profile-section')}
              sx={{
                px: 3,
                borderRadius: 999,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              View Profile
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={scrollToSection('hosted-section')}
              sx={{
                px: 3,
                borderRadius: 999,
                textTransform: 'uppercase',
                letterSpacing: 1,
                borderColor: 'rgba(226,232,240,0.7)',
              }}
            >
              Hosted Artworks
            </Button>
            {isGalleryHost && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={scrollToSection('represented-artists-section')}
                sx={{
                  px: 3,
                  borderRadius: 999,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  borderColor: 'rgba(226,232,240,0.7)',
                }}
              >
                Represented Artists
              </Button>
            )}
            {isGalleryHost && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={scrollToSection('represented-artworks-section')}
                sx={{
                  px: 3,
                  borderRadius: 999,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  borderColor: 'rgba(226,232,240,0.7)',
                }}
              >
                Represented Artworks
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* Profile section */}
      <Box
        id="profile-section"
        component="section"
        sx={{
          py: { xs: 6, md: 8 },
          background: 'transparent',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(148,163,184,0.9)',
              letterSpacing: 2,
            }}
          >
            Profile
          </Typography>
          <Typography
            variant="h4"
            sx={{ mb: 3, fontWeight: 600, color: 'grey.50' }}
          >
            About the Host
          </Typography>

          <HostProfileCard user={host} />
        </Container>
      </Box>

      {/* Hosted artworks section */}
      <Box
        id="hosted-section"
        component="section"
        sx={{
          py: { xs: 6, md: 8 },
          background: 'transparent',
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(148,163,184,0.9)',
              letterSpacing: 2,
            }}
          >
            Hosted Artworks
          </Typography>
          <Typography
            variant="h4"
            sx={{ mb: 3, fontWeight: 600, color: 'grey.50' }}
          >
            Artworks Hosted by {fullName}
          </Typography>

          <HorizontalSlider
            items={hostedArtworks}
            renderItem={(item) => {
              const art = normalizeArtworkForCard(item);
              return <ArtworkCard art={art} showActions />;
            }}
            emptyMessage="No Artflow-hosted artworks to display."
            sliderGap={sliderGap}
          />
        </Container>
      </Box>

      {/* Gallery represented artists section */}
      {isGalleryHost && (
        <Box
          id="represented-artists-section"
          component="section"
          sx={{
            py: { xs: 6, md: 8 },
            background: 'transparent',
          }}
        >
          <Container maxWidth="xl">
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(148,163,184,0.9)',
                letterSpacing: 2,
              }}
            >
              Gallery Artists
            </Typography>
            <Typography
              variant="h4"
              sx={{ mb: 3, fontWeight: 600, color: 'grey.50' }}
            >
              Gallery Represented Artists
            </Typography>

            <HorizontalSlider
              items={managedArtistsWithIds}
              renderItem={(link) => {
                const artistId = link.artist || link.artist_id;
                if (!artistId) return null;
                return (
                  <MiniArtistCard
                    id={artistId}
                    onViewProfile={() => navigate(`/artists/${artistId}`)}
                  />
                );
              }}
              emptyMessage="No represented artists to display."
              sliderGap={sliderGap}
            />
          </Container>
        </Box>
      )}

      {/* Gallery represented artworks section */}
      {isGalleryHost && (
        <Box
          id="represented-artworks-section"
          component="section"
          sx={{
            py: { xs: 6, md: 8 },
            background: 'transparent',
          }}
        >
          <Container maxWidth="xl">
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(148,163,184,0.9)',
                letterSpacing: 2,
              }}
            >
              Gallery Artworks
            </Typography>
            <Typography
              variant="h4"
              sx={{ mb: 3, fontWeight: 600, color: 'grey.50' }}
            >
              Gallery Represented Artworks
            </Typography>

            <HorizontalSlider
              items={representedArtworks}
              renderItem={(a) => {
                const art = normalizeArtworkForCard(a);
                return (
                  <ArtworkCard
                    art={art}
                    showActions
                    // Public view: just display, don’t expose gallery-management controls
                    canHostManage={false}
                  />
                );
              }}
              emptyMessage="No represented artworks to display."
              sliderGap={sliderGap}
            />
          </Container>
        </Box>
      )}
    </Box>
  );
}
